import { fimCdnAnimateUrl } from '@utils/fimUtils'
import { Flex } from 'antd'
import React, { useEffect, useRef } from 'react'

import { ComponentLibrary } from '@suntex.classic/adobe-animate'
import { extendSymbols } from '@suntex.classic/symbols'
import { $$$ } from '@suntex.core/text'
import { asPath } from '@suntex.core/text/extensions'
import { waitFor } from '@suntex.core/utils'
import { ApiClient } from '@suntex.fim/api-client'
import { useFramework } from '@suntex.fim/framework'
import '@suntex.fim/global'
import { addScript, useEffectAddScriptOnce } from '@suntex.fim/ui'

//type BooleanInteger = 0 | 1
type BooleanIntegerString = '0' | '1'

type LoaderEvent = { item: createjs.LoadItem; target: unknown; result: object }

export type ICanvasParameters = {
  [K in string]: unknown
} & {
  preview?: BooleanIntegerString
  soundDisabled?: BooleanIntegerString
  hubUrl?: string
  hubPath?: never // to prevent accidentally using the wrong parameter name, since we used to use both and they are easy to mix up
  nextGameUrl?: string | null
}

type ProgramFunction = (
  stage: createjs.Stage,
  exportRoot: createjs.DisplayObject,
  lib: ComponentLibrary
) => any

export interface CanvasProps<TCanvasParameters extends ICanvasParameters> {
  name: string
  width: number
  height: number
  parameters: TCanvasParameters
  bgColor?: string
  jsSrc?: string
  images?: { [key: string]: string }
  compositionId?: string
  mute?: boolean
  preview?: boolean
  ProgramClass?: ProgramFunction
  label?: string
  // Must be stable: excluded from the memo comparison below.
  onCanvasKeyDown?: CanvasKeyDownHandler
}

export type CanvasKeyDownHandler = (
  event: React.KeyboardEvent<HTMLCanvasElement>,
  exportRoot: createjs.DisplayObject
) => void

export class CanvasProperties<
  TCanvasParameters extends ICanvasParameters
> implements CanvasProps<TCanvasParameters> {
  public name = ''
  // eslint-disable-next-line no-magic-numbers
  public width = 800
  // eslint-disable-next-line no-magic-numbers
  public height = 600
  public bgColor = '#FFFFFF'
  public jsSrc = ''
  public images: { [key: string]: string } = {}
  public compositionId = ''

  public readonly parameters: TCanvasParameters = {
    preview: '0',
    soundDisabled: '0'
  } as TCanvasParameters

  public preview = false
  public mute = false

  public ProgramClass?: ProgramFunction
  public label?: string
  public onCanvasKeyDown?: CanvasKeyDownHandler
}

export const Canvas = React.memo(
  function Canvas<TCanvasParameters extends ICanvasParameters>(
    props: CanvasProps<TCanvasParameters>
  ) {
    return (
      <Flex
        justify="center"
        className={`container game-container ${props.name}`}
      >
        {props.jsSrc ? (
          <CanvasEmbedded
            {...{
              ...new CanvasProperties(),
              ...props,
              jsSrc: props.jsSrc ?? `${props.name}.js`
            }}
          />
        ) : (
          <CanvasPlaceholder />
        )}
      </Flex>
    )
  },
  (prev, next) =>
    prev.name === next.name &&
    prev.width === next.width &&
    prev.height === next.height &&
    prev.bgColor === next.bgColor &&
    prev.jsSrc === next.jsSrc &&
    prev.compositionId === next.compositionId &&
    JSON.stringify(prev.parameters) === JSON.stringify(next.parameters) &&
    prev.preview === next.preview &&
    prev.mute === next.mute &&
    prev.ProgramClass === next.ProgramClass &&
    prev.label === next.label
)

export default Canvas

function CanvasEmbedded<TCanvasParameters extends ICanvasParameters>(
  props: CanvasProperties<TCanvasParameters>
) {
  const { http } = useFramework()
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const exportRootRef = useRef<createjs.DisplayObject | null>(null)
  const displayClassName = props.name
  const programClassName = `${props.name}_prog`

  function getComposition() {
    const compositionId = props.compositionId || ''
    const key =
      compositionId === ''
        ? Object.keys(window.AdobeAn!.compositions!)[0]
        : compositionId

    return window.AdobeAn!.getComposition(key)!
  }

  // the CreateJS export
  useEffectAddScriptOnce(fimCdnAnimateUrl(props.jsSrc))

  useEffect(() => {
    let removeScript = () => {}

    LoadUtil.get = function (
      url: string,
      callback,
      dataType = 'json',
      thisArg
    ) {
      if (dataType !== 'json') {
        throw new Error('Unsupported data type: ' + dataType)
      }
      http
        .get(
          $$$(url, asPath()).withParams({
            'api-version': ApiClient.DEFAULT_API_VERSION
          }).$$
        )
        .then((response) => {
          if (callback) {
            callback.call(thisArg, JSON.parse(response.data))
          }
        })
        .catch((e) => {
          // eslint-disable-next-line no-console
          console.error(e)
          if (callback) {
            callback.call(thisArg, null)
          }
        })
    }

    LoadUtil.post = function (
      url: string,
      data,
      callback: (response) => void,
      dataType,
      thisArg
    ) {
      //if (dataType !== 'json') {
      //  throw new Error('Unsupported data type: ' + dataType)
      //}

      if (!dataType) dataType = 'text'

      http
        .post(
          $$$(url, asPath()).withParams({
            'api-version': ApiClient.DEFAULT_API_VERSION
          }).$$,
          data
        )
        .then((response) => {
          if (callback) {
            if (dataType === 'json') {
              return callback.call(thisArg, JSON.parse(response.data))
            } else {
              callback.call(thisArg, response.data)
            }
          }
        })
        .catch((e) => {
          // eslint-disable-next-line no-console
          console.error(e)
          if (callback) {
            callback.call(thisArg, null)
          }
        })
    }

    async function load() {
      await waitFor(() => !!window.AdobeAn)
      // eslint-disable-next-line no-console
      console.debug('AdobeAn', window.AdobeAn)

      await waitFor(() => !!getComposition())
      const comp = getComposition()
      const lib = comp.getLibrary()
      window.lib = lib
      // this script must be called after the CreateJS graphics script has run, in order to extend the display classes
      extendSymbols()

      if (!props.ProgramClass) {
        removeScript = addScript(
          fimCdnAnimateUrl(props.jsSrc.replace('.js', '.jsfl'))
        )
        await waitFor(() => !!window[programClassName])
        // eslint-disable-next-line no-console
        console.debug(programClassName, window[programClassName])
      }

      loadCanvas()
    }
    void load()
    return () => {
      removeScript()
    }
  }, [http])

  //load the named canvas
  function loadCanvas() {
    ////if print mode, set up for printing
    //if (isPrintMode())
    //{
    //    $("body :not(canvas)").hide();
    //    $("canvas").parents().show();
    //    $("#contentHeader, #contentHeader *").show();
    //}

    // const container = document.querySelector(`.container.${props.name}`)!
    //canvas = container.querySelector(`canvas`)
    //anim_container = container.querySelector(`animation_container`);
    //dom_overlay_container = container.querySelector(`dom_overlay_container`);

    createjs.MotionGuidePlugin.install()

    const comp = getComposition()
    const lib = comp.getLibrary()

    if (lib.properties.manifest && lib.properties.manifest.length > 0) {
      //patch up the manifest object src property to map the Animate format to our site directory structure
      for (const obj of lib.properties.manifest) {
        if (obj.src.startsWith('images')) {
          obj.src = fimCdnAnimateUrl(obj.src)
        }
      }

      const loader = new createjs.LoadQueue(false)

      loader.addEventListener('fileload', function (evt) {
        handleFileLoad(evt as LoaderEvent)
      })

      loader.addEventListener('complete', function (evt) {
        handleComplete(props, evt as LoaderEvent)
      })

      for (const item of lib.properties.manifest) {
        item.crossOrigin = true
      }

      loader.loadManifest(lib.properties.manifest)
    } else {
      handleComplete(props)
    }

    function handleFileLoad(evt: LoaderEvent) {
      const images = comp.getImages()
      if (evt && evt.item.type == 'image') {
        images[evt.item.id] = evt.result
      }
    }

    function handleComplete(
      props: CanvasProperties<TCanvasParameters>,
      evt?: LoaderEvent
    ) {
      const lib = comp.getLibrary()

      if (evt) {
        const spriteSheet = comp.getSpriteSheet()

        const queue = evt.target as createjs.LoadQueue

        //ssMetadata is of type Metadata | undefined array, but we will filter out any undefined ones, so:

        //define the type, because it's private in adobe.ts
        type Metadata = {
          name: string
          frames: any
        }

        const ssMetadata = (lib.ssMetadata ?? []).filter(
          (m) => !!m
        ) as Metadata[]

        for (const metadata of ssMetadata) {
          spriteSheet[metadata.name] = new createjs.SpriteSheet({
            images: [queue.getResult(metadata.name)],
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            frames: metadata.frames
          })
        }
      }

      //This function is always called, irrespective of the content. You can use the variable "stage" after it is created in token create_stage.
      const DisplayClass = lib[displayClassName]
      const exportRoot = new DisplayClass()
      exportRootRef.current = exportRoot
      const stage = new lib.Stage(canvasRef.current)
      window.stage = stage

      stage.update()

      //enable touch support
      createjs.Touch.enable(stage)

      Object.keys(props.parameters).forEach(function (key) {
        UIUtil.addParameter(key, props.parameters[key])
      })

      //TODO:make sure this is the right way to handle params
      UIUtil.addParameter('preview', props.preview ? '1' : '0')
      UIUtil.addParameter('soundDisabled', props.mute ? '1' : '0')

      window.AdobeAn!.compositionLoaded(lib.properties.id)

      // eslint-disable-next-line no-magic-numbers
      stage.enableMouseOver(50)

      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const ProgramClass = props.ProgramClass ?? window[programClassName]
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
      const prog: { run: () => void } = new ProgramClass(stage, exportRoot, lib)

      stage.addChild(exportRoot)
      stage.update()
      //Registers the "tick" event listener.
      createjs.Ticker.framerate = lib.properties.fps
      createjs.Ticker.addEventListener('tick', stage)

      prog.run()
    }
  }

  // Cleanup

  useEffect(() => {
    return () => {
      LoadUtil.stopAllSounds()
      UIUtil.cancelAllDelays()
      UIUtil.clearAllIntervals()
      UIUtil.stopAllTweensAndDelays()
      UIUtil.stopTimingValidation()
    }
  }, [])

  return (
    <div
      className="canvas-wrapper"
      style={{
        margin: 'auto',
        padding: '0px',
        position: 'relative',
        textAlign: 'left'
      }}
    >
      <div className="animation_container">
        <canvas
          ref={canvasRef}
          className="canvas"
          width={props.width}
          height={props.height}
          style={{
            backgroundColor: props.bgColor
          }}
          aria-label={props.label ?? props.name}
          {...(props.onCanvasKeyDown && {
            tabIndex: 0,
            // CreateJS prevents default on press, which blocks the browser from moving focus here.
            onPointerDown: () =>
              canvasRef.current?.focus({ preventScroll: true }),
            onKeyDown: (e: React.KeyboardEvent<HTMLCanvasElement>) => {
              if (exportRootRef.current) {
                props.onCanvasKeyDown?.(e, exportRootRef.current)
              }
            }
          })}
        ></canvas>
        <div
          className="dom_overlay_container"
          style={{
            pointerEvents: 'none',
            overflow: 'hidden',
            width: props.width,
            height: props.height,
            position: 'absolute',
            left: '0px',
            top: '0px',
            display: 'block'
          }}
        ></div>
      </div>
    </div>
  )
}

function CanvasPlaceholder() {
  return (
    <div
      style={{
        backgroundColor: '#EEE',
        textAlign: 'center',
        borderRadius: '20px',
        padding: '30px',
        margin: '20px auto'
      }}
    >
      This game is being updated and will be posted upon completion.
    </div>
  )
}
